// Pure helpers for the single-metric panel's "compare against prior period" mode.
// The prior period is computed entirely client-side by shifting the resolved
// timeframe's start/end dates; results are fed to a second KPI summary call.

// "All Time" has no meaningful prior period, but it's hard to identify: its resolved
// dates look like any wide custom range, and timeframe ids are NOT reliable — admin-
// created timeframes get per-client UUIDs (e.g. the YTD preset observed as
// "1747020f-…"), and some clients don't have an All Time preset at all. We best-effort
// match the cube19-default id below; when it doesn't match (UUID / absent), the
// checkbox simply stays enabled and load degrades gracefully — enabling it on a true
// All-Time timeframe yields an empty prior window, so no % change is shown (no crash,
// no wrong number). So this is a nicety for default installs, not a correctness guard.
const ALL_TIME_ID = "all_time";

const normalise = value => (value == null ? "" : String(value)).replace(/[^a-z0-9]/gi, "").toLowerCase();

export const isAllTime = timeframe =>
  !!timeframe && normalise(timeframe.get("id")) === normalise(ALL_TIME_ID);

// Year-to-date is detected structurally, not by id: it starts on Jan 1 and ends
// before the end of that same year (an in-progress year). "This year" (Jan 1 →
// Dec 31) is deliberately not treated as YTD — a plain day-shift already maps it
// to last year.
const isYearToDate = timeframe => {
  const start = timeframe.get("start");
  const end = timeframe.get("end");
  if (!start || !end || !start.isValid() || !end.isValid()) return false;
  const startsOnJan1 = start.month() === 0 && start.date() === 1;
  const endsBeforeYearEnd = end.year() === start.year() && (end.month() < 11 || end.date() < 31);
  return startsOnJan1 && endsBeforeYearEnd;
};

// Returns {start, end} moments for the prior period, or null when not computable.
// - All time: not computable.
// - Year to date (special case): Jan 1 last year → the same day last year.
// - Everything else (last N days, week/month/quarter/year, rolling 12m, custom):
//   the adjacent, equal-length window immediately before `start` (day-shift).
export const computePriorTimeframe = timeframe => {
  if (!timeframe || isAllTime(timeframe)) return null;

  const start = timeframe.get("start");
  const end = timeframe.get("end");
  if (!start || !end || !start.isValid() || !end.isValid()) return null;

  if (isYearToDate(timeframe)) {
    return {
      start: start.clone().subtract(1, "year"),
      end: end.clone().subtract(1, "year")
    };
  }

  const durationDays = end.diff(start, "days");
  return {
    start: start.clone().subtract(durationDays + 1, "days"),
    end: start.clone().subtract(1, "day")
  };
};

export const isPriorPeriodComputable = timeframe => computePriorTimeframe(timeframe) !== null;
